import type { MutationUpdaterFn } from 'apollo-client';

import type { MutationFunctionInteropSupportedOptions } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { expValEquals } from '@confluence/feature-experiments';

import {
	resolveCommentInCache,
	type UpdateCommentCallback,
	type ResolveInlineCommentMutationResultData,
	type ResolveCommentsMutationResultData,
	type ReopenCommentMutationResultData,
} from './useUpdateCommentInCache.ts';
import type { GraphQLContentStatus } from './__generated__/useAllCommentsDataQuery.graphql.ts';
import type { Comment, CommentReply } from './useAllCommentsData.ts';

// The canonical comment/reply shapes for the Comments Data Manager, derived from the generated
// `useAllCommentsData` query types. `CommentReply` is nullable (`... | null`); `NonNullable`
// strips the `null` so `ReplyData` is always a concrete reply.
export type ReplyData = NonNullable<CommentReply> & {
	/** @deprecated Retained for legacy CommentsDataMap consumers. */
	wasRemovedByAnotherUser: boolean;
};

export type CommentData = Comment & {
	type: CommentType;
	isOpen: boolean;
	/**
	 * @deprecated The launched Comments Data Manager path no longer tracks this state. Retained as
	 * `false` for consumers migrating from the legacy comments-data contract.
	 */
	wasRemovedByAnotherUser: boolean;
	replies: ReplyData[];
};

export enum CommentType {
	INLINE = 'inline',
	GENERAL = 'general',
}

/**
 * GraphQL `CommentsType` schema discriminator for a comment thread. Mirrors the platform
 * comments GraphQL enum so navigation/consumer code shares a single source of truth.
 */
export type CommentsType = 'FOOTER' | 'INLINE' | 'RESOLVED' | 'UNRESOLVED';

export type CommentsDataMap = {
	inline: Record<string, CommentData>;
	general: Record<string, CommentData>;
};

export type CategorizedComments = {
	allFilteredComments: Comment[];
	openInlineComments: Comment[];
	openFooterComments: Comment[];
	resolvedComments: Comment[];
	openComments: Comment[];
	commentsDataMap: CommentsDataMap;
	flatCommentsMap: Record<string, Comment | CommentReply>;
	flatMarkerRefMap: Record<string, Comment>;
	hasNestedComments: boolean;
	commentCount: number;
	hasAnyComments: boolean;
};

// This is same as `defaultReactionsSummary` defined in
// next/packages/inline-comments-common/src/inlineCommentsCommonUtils.ts,
// but importing that causes circular dependency between packages.
export type EmptyReactionsSummary = {
	ari: string;
	containerAri: string;
	reactionsCount: number;
	reactionsSummaryForEmoji: any[];
	__typename: 'ReactionsSummaryResponse';
};
export const emptyReactionsSummary: {
	ari: string;
	containerAri: string;
	reactionsCount: number;
	reactionsSummaryForEmoji: never[];
	__typename: string;
} = {
	ari: '',
	containerAri: '',
	reactionsCount: 0,
	reactionsSummaryForEmoji: [],
	__typename: 'ReactionsSummaryResponse',
};

// This is a fake numerical ID used as the `id` of an optimistically created
// comment or reply (when optimistic save comment is enabled).
// This large integer should prevent any conflict with actual comment ids.
export const OPTIMISTIC_SAVE_COMMENT_ID = '998765432100';

// To avoid Sonar errors about nested ternaries, as the false functions (the legacy code paths for
// cache updates) often contain ternaries.
export const selectUpdateFunction = <TResultType>(
	condition: boolean,
	trueFn?: MutationFunctionInteropSupportedOptions<TResultType, never>['update'],
	falseFn?: MutationFunctionInteropSupportedOptions<TResultType, never>['update'],
): MutationUpdaterFn<TResultType> | undefined => (condition ? trueFn : falseFn);

export const findCommentInData = (
	commentData: Comment[],
	commentId: string,
): Comment | undefined => {
	// Search in retreived comments and their replies
	for (const comment of commentData) {
		// Check if this is the comment we're looking for
		if (comment?.id === commentId) {
			return comment;
		}

		// Check if it's in the replies
		if (comment?.replies) {
			const foundReply = comment.replies.find((reply) => reply?.id === commentId);
			if (foundReply) {
				return foundReply as Comment;
			}
		}
	}

	// else comment not found
	return undefined;
};

/**
 * Finds the root (top-level) comment that contains a reply with the given ID
 * Note that the reply arrays are actually flat arrays of Comment objects - nested comments are not
 * actually nested in the GQL data schema but stored flat in the root parent's replies array
 * with the parentId field that indicates which comment they really reply to (we build the actual
 * nested structure elsewhere in the FE). So this function finds where you should add a reply -
 * even a nested reply - in the schema (or undefined if the parent is not found in the data).
 * @param commentData - Array of root level comments to search through
 * @param replyParentId - The parent ID of the reply to find
 * @returns The root comment that contains the reply, or undefined if not found
 */
export const findRootCommentForReply = (
	commentData: Comment[],
	replyParentId: string,
): Comment | undefined => {
	// Search through each root level comment's replies

	for (const comment of commentData) {
		// Return this comment if it is the parent of the reply, else check if this comment has replies
		// and if any of them have the matching parentId
		if (
			comment?.id === replyParentId ||
			comment?.replies.find((reply) => reply?.id === replyParentId)
		) {
			return comment;
		}
	}

	// else comment not found
	return undefined;
};

export const findCommentByMarkerRef = (
	commentData: Comment[],
	markerRef: string,
): Comment | undefined => {
	// Search in retrieved comments and their replies
	for (const comment of commentData) {
		// Check if this comment's marker ref matches
		const location = comment?.location as { inlineMarkerRef?: string } | null;
		if (location?.inlineMarkerRef === markerRef) {
			return comment;
		}
	}
	return undefined;
};

/**
 * Helper function to find and filter a comment from a data source.
 * Finds the comment by commentId or markerRef, then filters by contentStatus.
 *
 * @param comments - Array of comments to search through
 * @param commentId - Optional comment ID to find
 * @param markerRef - Optional marker ref to find (only used when commentId is NOT supplied)
 * @param contentStatus - Array of content statuses to filter by
 * @returns The found and filtered comment, or undefined if not found or filtered out
 */
export const findAndFilterComment = (
	comments: Comment[],
	commentId: string | undefined,
	markerRef: string | undefined,
	contentStatus: GraphQLContentStatus[],
): Comment | undefined => {
	if (comments.length === 0) {
		return undefined;
	}

	let foundComment: Comment | undefined;
	if (commentId) {
		foundComment = findCommentInData(comments, commentId);
	} else if (markerRef) {
		foundComment = findCommentByMarkerRef(comments, markerRef);
	}

	if (!foundComment) {
		return undefined;
	}

	// Filter by contentStatus before returning
	const matchesContentStatusFilter = (comment: Comment | undefined): boolean => {
		if (!comment || !comment.contentStatus) {
			return false;
		}
		// Convert comment's contentStatus to uppercase to match GraphQLContentStatus format
		const commentStatus = comment.contentStatus.toUpperCase() as GraphQLContentStatus;
		return contentStatus.includes(commentStatus);
	};

	if (matchesContentStatusFilter(foundComment)) {
		return foundComment;
	}
	return undefined;
};

/**
 * Updates an inline comment fragment as resolved or reopened after a resolveInlineComment()
 * mutation, based on the result of a "resolved" property in the mutation result.  This is
 * effectively a wrapper around resolveCommentInCache() that extracts the relevant properties from
 * the mutation result - but is in a separate file so it can be unit tested with a spied
 * resolveCommentInCache(), since you can't mock or spy on a function that is called directly
 * within the same file.
 * @param comment - the current cached inline comment fragment
 * @param mutationResult - the result of the resolveInlineComment() mutation
 * @returns - the updated comment fragment, to be written back to the cache
 */
export const resolveInlineCommentInCacheCallback: UpdateCommentCallback<
	ResolveInlineCommentMutationResultData
> = (comment, mutationResult) => {
	const resolveInlineComment = mutationResult?.data?.resolveInlineComment;
	const mutationStatus = resolveInlineComment?.status ?? false;
	const resolveProperties = resolveInlineComment?.resolveProperties ?? undefined;

	return resolveCommentInCache({ comment, mutationStatus, resolveProperties });
};

/**
 * Updates a comment fragment as resolved. after a confluence_resolveComments() mutation, based on
 * the result of a "resolved" property in the mutation result.  This is effectively a wrapper
 * around resolveCommentInCache() that extracts the relevant properties from the mutation result -
 * but is in a separate file so it can be unit tested with a spied resolveCommentInCache(), since
 * you can't mock or spy on a function that is called directly within the same file.
 *
 * Although the mutation can resolve multiple comments at once, this function assumes that ONLY
 * ONE COMMENT PER MUTATION CALL is being updated. That is necessary because the updateCommentInCache()
 * updater function only works on a single comment - and it is okay because the current code only
 * ever resolves one comment at a time with this mutation.  Therefore this function finds the first
 * comment resolution state from the array in the mutation result.
 * @param comment - the current cached inline comment fragment
 * @param mutationResult - the result of the confluence_resolveComments() mutation
 * @returns - the updated comment fragment, to be written back to the cache
 */
export const resolveCommentInCacheCallback: UpdateCommentCallback<
	ResolveCommentsMutationResultData
> = (comment, mutationResult) => {
	const commentResolutionStates =
		mutationResult?.data?.confluence_resolveComments?.commentResolutionStates;
	if ((commentResolutionStates?.length ?? 0) > 1) {
		throw new Error(
			'resolveCommentInCacheCallback() only supports one comment resolution state per mutation call',
		);
	}
	const firstResolutionState = commentResolutionStates?.[0] ?? undefined;
	const mutationStatus = firstResolutionState?.status ?? false;
	const partialResolveProperties = firstResolutionState?.resolveProperties ?? undefined;

	// We need to ensure that resolveProperties contains all non-optional properties so we can safely
	// spread it into the comment location resolve properties, so choose defaults for any missing
	// ones.  This is needed because some of the mutations don't request all the properties in their
	// responses.
	const resolveProperties = partialResolveProperties
		? {
				resolved: true, // This mutation only resolves comments
				isDangling: false, // Assumes resolved comments are never dangling
				// These are nullable in the schema, but included to make Typescript happy
				resolvedUser: undefined,
				resolvedFriendlyDate: undefined,
				resolvedTime: undefined,
				// Add any non-null/undefined properties from the mutation result
				...partialResolveProperties,
			}
		: undefined;

	return resolveCommentInCache({ comment, mutationStatus, resolveProperties });
};

/**
 * Updates a comment fragment as reopened after a confluence_reopenComment() mutation, based on the
 * result of a "resolved" property in the mutation result.  This is effectively a wrapper around
 * resolveCommentInCache() that extracts the relevant properties from the mutation result - but is
 * in a separate file so it can be unit tested with a spied resolveCommentInCache(), since you
 * can't mock or spy on a function that is called directly within the same file.
 * @param comment - the current cached inline comment fragment
 * @param mutationResult - the result of the confluence_reopenComment() mutation
 * @returns - the updated comment fragment, to be written back to the cache
 */
export const reopenCommentInCacheCallback: UpdateCommentCallback<
	ReopenCommentMutationResultData
> = (comment, mutationResult) => {
	// (The property is called "commentResolutionStates" but there is only one item per mutation call)
	const commentResolutionStates =
		mutationResult?.data?.confluence_reopenComment?.commentResolutionStates;

	if (commentResolutionStates) {
		const mutationStatus = commentResolutionStates.status ?? false;
		const partialResolveProperties = commentResolutionStates.resolveProperties ?? undefined;

		// We need to ensure that resolveProperties contains all non-optional properties so we can safely
		// spread it into the comment location resolve properties, so choose defaults for any missing
		// ones. This is needed because some of the mutations don't request all the properties - or even
		// the commentResolutionStates property at all in their responses.
		const resolveProperties = {
			resolved: false, // This mutation only reopens comments
			isDangling: false, // Assumes reopened comments are never dangling
			// These are nullable in the schema, but included to make Typescript happy
			resolvedUser: undefined,
			resolvedFriendlyDate: undefined,
			resolvedTime: undefined,
			// Add any non-null/undefined properties from the mutation result
			...partialResolveProperties,
		};

		return resolveCommentInCache({ comment, mutationStatus, resolveProperties });
	}

	// Else commentResolutionStates wasn't returned - assume failure to reopen
	return resolveCommentInCache({ comment, mutationStatus: false, resolveProperties: undefined });
};

/*
 * Helper function to determine the appropriate content status values to use for comments queries.
 * Returns different status filters based on the current page mode:
 * - VIEW pages: Returns CURRENT and ARCHIVED (excludes DRAFT comments)
 * - EDIT/LIVE pages: Returns CURRENT, ARCHIVED, and DRAFT
 *
 * NOTE - After investigation on the BE, it shouldn't be possible for archived comments to be returned on non-archived pages (and vis versa)
 * Pulling useContentStatus is a relatively heavy operation, and should be avoided if possible, hence the change in defensive programming here
 *
 * @param pageMode - The current page mode (VIEW, EDIT, or LIVE)
 * @returns Array of GraphQLContentStatus values to use for filtering comments
 */
export const buildDefaultCommentsContentStatusFilters = (
	pageMode: PageMode,
): GraphQLContentStatus[] => {
	// On view pages, exclude draft comments
	if (pageMode === PageMode.VIEW) {
		return ['CURRENT', 'ARCHIVED'];
	}

	// On edit and live pages, include all statuses
	return ['CURRENT', 'ARCHIVED', 'DRAFT'];
};

// Utility function to check if a comment is resolved
export const isCommentResolved = (comment: Comment): boolean => {
	const location = comment?.location;
	if (location) {
		if ('inlineResolveProperties' in location) {
			return location.inlineResolveProperties?.resolved ?? false;
		} else if ('commentResolveProperties' in location) {
			return location.commentResolveProperties?.resolved ?? false;
		}
	}
	return false;
};

// Utility function to parse hasNestedComments from commentsDataMap
export const parseHasNestedComments = (commentsDataMap: CommentsDataMap): boolean => {
	try {
		const allGeneralComments = Object.values(commentsDataMap.general);

		// To see if there are nested comments, we need to check whether there are any replies whose parentID is not equal to the parent Comment's ID
		// This is because commentsDataMap.general is a flat map of parent comment --> everything below it
		// But each comment has it's own parent id which may be different from it's top level parent
		return allGeneralComments.some((comment) => {
			return comment?.replies?.some((reply) => reply && reply.parentId !== comment.id);
		});
	} catch {
		// Default to false if somehow something goes wrong
		return false;
	}
};

// Utility function to categorize comments by type
export const categorizeComments = (
	comments: Comment[],
	contentStatusFilters: GraphQLContentStatus[],
): CategorizedComments => {
	const allFilteredComments: Comment[] = [];
	const openComments: Comment[] = [];
	const openInlineComments: Comment[] = [];
	const openFooterComments: Comment[] = [];
	const resolvedComments: Comment[] = [];

	// Transform array-based data to map structure for legacy compatibility
	const commentsDataMap: CommentsDataMap = {
		inline: {},
		general: {},
	};

	// Build flat map of all comments (including replies) for O(1) lookup by commentId
	const flatCommentsMap: Record<string, Comment | CommentReply> = {};
	// Build flat map of inline comments keyed by markerRef for O(1) lookup
	const flatMarkerRefMap: Record<string, Comment> = {};

	for (const comment of comments) {
		// Filter by content status, which is a string literal type (that is, a set of allowed
		// strings), so be cautious if the definitions or schema change
		if (
			comment &&
			contentStatusFilters.includes(comment.contentStatus?.toUpperCase() as GraphQLContentStatus)
		) {
			allFilteredComments.push(comment);

			// Add comment to flat map by commentId
			if (comment?.id) {
				flatCommentsMap[comment.id] = comment;
			}

			// Add inline parent comment to flatMarkerRefMap by markerRef (only parent comments, not replies)
			if (comment?.location?.type === 'INLINE' && !comment?.parentId) {
				const location = comment.location as { inlineMarkerRef?: string } | null;
				if (location?.inlineMarkerRef) {
					flatMarkerRefMap[location.inlineMarkerRef] = comment;
				}
			}

			// Add all replies to flat map
			if (comment?.replies) {
				for (const reply of comment.replies) {
					if (reply?.id) {
						flatCommentsMap[reply.id] = reply;
					}
				}
			}

			const isOpen = !isCommentResolved(comment);
			const commentType =
				comment.location?.type === 'INLINE' ? CommentType.INLINE : CommentType.GENERAL;

			const commentWithProperties: CommentData = {
				...comment,
				type: commentType,
				isOpen,
				wasRemovedByAnotherUser: false,
				replies: comment.replies
					?.filter((reply): reply is CommentReply => reply !== null && reply !== undefined)
					.map((reply) => ({
						...reply,
						wasRemovedByAnotherUser: false,
					})) as ReplyData[],
			};

			if (isOpen) {
				openComments.push(comment);

				if (comment?.location?.type === 'INLINE') {
					openInlineComments.push(comment);
				} else if (comment?.location?.type === 'FOOTER') {
					openFooterComments.push(comment);
				}
				// else unknown or missing location type, do nothing
			} else {
				resolvedComments.push(comment);
			}

			if (comment.location?.type === 'INLINE') {
				const threadKey =
					'inlineMarkerRef' in comment.location
						? comment.location.inlineMarkerRef || comment.id
						: comment.id;
				commentsDataMap.inline[threadKey] = commentWithProperties;
			} else if (comment.location?.type === 'FOOTER') {
				commentsDataMap.general[comment.id] = commentWithProperties;
			}
		}
	}

	const hasNestedComments = parseHasNestedComments(commentsDataMap);
	const commentCount = allFilteredComments.length;
	const hasAnyComments = commentCount > 0;

	return {
		allFilteredComments,
		openComments,
		openInlineComments,
		openFooterComments,
		resolvedComments,
		commentsDataMap,
		flatCommentsMap,
		flatMarkerRefMap,
		hasNestedComments,
		commentCount,
		hasAnyComments,
	};
};

// Setting ignoreNoNetworkError to true in the context allows GQL mutation
// to throw NoNetworkError from the Promise, so we can catch it and show
// a proper error message to the user. See COMMENTS-6859 for details.
// This function is identical to the one in next/packages/comments-util/src/errorUtils.ts,
// since importing from there causes circular dependency between packages.
export const buildMutationContextForNoNetworkError = (): {
	context: {
		fetchOptions: {
			ignoreNoNetworkError: boolean;
		};
	};
} => {
	return {
		context: {
			fetchOptions: {
				ignoreNoNetworkError: expValEquals(
					'confluence_fe_disable_comment_if_offline_fix',
					'isEnabled',
					true,
				),
			},
		},
	};
};
